/**
 * GROWTH-01 — the decision half of the web-to-app prompt.
 *
 * Everything here is a pure function over values the caller reads from the
 * browser. Nothing in this module touches `window`, `document`, `localStorage`,
 * `sessionStorage`, `navigator` or Capacitor, and that is load-bearing three
 * times over:
 *
 *   1. VIKE SSR. src/pages/property/@slug/ is server-rendered. A module that
 *      reads a browser global at import time takes the server render down with
 *      it; one that reads it inside a function called from an effect cannot.
 *   2. THE TESTS. pages/__tests__/mobileAppPrompt.test.ts runs under plain
 *      `npx tsx` — no DOM, no bundler, no `import.meta.env`. Importing
 *      @capacitor/core or reading `window` here would make the rules
 *      untestable, so the rules live where a test can reach them.
 *   3. ONE PLACE PER RULE. The component decides *when* to ask the browser;
 *      this file decides what the answer means.
 *
 * The browser half is split in two: src/lib/appBannerSession.ts is the only
 * code that reads or writes the session, and src/components/MobileAppPrompt.tsx
 * is the React shell.
 */

import { DEEP_LINK_ALLOWLIST } from '../../lib/deepLinkRoutes';

// -----------------------------------------------------------------------------
// IDENTIFIERS
// -----------------------------------------------------------------------------

/**
 * Follows the mandatory `afriqahome_*` key convention (config/constants.ts,
 * `afriqahome_access_token`, `afriqahome_home_prompt_dismissed`, …).
 *
 * ONE KEY, ONE SESSION. The value is JSON — see `AppBannerSession` — and it is
 * the whole memory this banner has: when the visitor was last active on the
 * site, and whether they have already answered the banner in this session.
 *
 * It lives in `sessionStorage`, not `localStorage`, because a session is a
 * visit (see `SESSION_IDLE_TIMEOUT_MS`): opening Afriqahome in a new tab, or
 * again after closing it, is a new visit, and sessionStorage is the storage
 * the browser scopes exactly that way. The idle timeout below covers the case
 * sessionStorage cannot — a tab left open on a phone for hours.
 */
export const APP_BANNER_SESSION_KEY = 'afriqahome_app_banner_session';

/**
 * The `localStorage` key the 14-day / 7-day cooldowns lived under until the
 * session model replaced them. It is never read — a "no" from that era is not
 * a "no" in this session — only removed, so that it does not sit in visitors'
 * storage forever.
 */
export const LEGACY_COOLDOWN_KEY = 'afriqahome_app_banner_dismissed_at';

/**
 * `localStorage`, set to the epoch ms of the tap the first time this browser
 * taps the banner's CTA, and never cleared. It is the web's only lasting
 * evidence that the phone may have the app — see `bannerVariant`.
 */
export const APP_BANNER_INSTALL_HINT_KEY = 'afriqahome_app_banner_install_hint';

/** Matches public/.well-known/assetlinks.json. Do not fork this string. */
export const ANDROID_PACKAGE_NAME = 'com.afriqahome.app';

/**
 * The host the Universal Links / App Links associations are published on
 * (PR #671). The intent URL must name this exact host or Android refuses to
 * match it against the verified App Link and falls through to the Play Store.
 * It is also the site android/app/src/main/res/values/strings.xml declares in
 * `asset_statements`, which is what lets Chrome answer "is the app installed".
 */
export const APP_LINK_HOST = 'www.afriqahome.com';

/**
 * The numeric App Store ID of the iOS app, supplied by Dirie.
 *
 * It is the only source of the App Store link — there is no other App Store URL
 * anywhere in this repository (searched: config/constants.ts, src/seo/*,
 * index.html, the .well-known files, every .ts/.tsx/.json/.md), so this is the
 * one place to change it.
 *
 * IT MUST STAY ALL DIGITS. `hasIosAppStoreId()` is false for anything else, and
 * `shouldRenderBanner()` then refuses iOS outright rather than pointing an
 * iPhone at `https://apps.apple.com/app/id{IOS_APP_STORE_ID}` — a 404 for every
 * iOS visitor. That guard is what made a placeholder safe to ship before this
 * value arrived, and it is what keeps a future placeholder, an empty string or
 * an `id`-prefixed copy-paste from becoming a broken link rather than no link.
 * pages/__tests__/mobileAppPrompt.test.ts fails if it ever stops being digits.
 * Android is unaffected by any of this.
 */
export const IOS_APP_STORE_ID = '6784167134';

// -----------------------------------------------------------------------------
// TIMING
// -----------------------------------------------------------------------------

/**
 * A SESSION, DEFINED.
 *
 * A session is one visit. It begins when Afriqahome is opened in a browser tab
 * and it ends when that tab is closed — or, if the tab is simply left open,
 * once the visitor has been away from it for thirty minutes. The thirty
 * minutes are GA4's own session rule (`session_timeout`, 30 minutes of
 * inactivity), chosen so that "once per session" here and "sessions" in the
 * analytics reports count the same thing.
 *
 * Answering the banner — the ✕ or the CTA — silences it for the rest of THAT
 * session and no longer. The next visit asks again, whether the visitor has
 * the app (the "open" banner) or not (the "get" banner). There is no longer
 * any cooldown measured in days.
 */
export const SESSION_IDLE_TIMEOUT_MS = 30 * 60 * 1000;

/**
 * How often an open, visible page renews the session's `lastSeenAt`. Well
 * inside the idle timeout, so a visitor who reads one long page without
 * navigating is never mistaken for one who left.
 */
export const SESSION_HEARTBEAT_MS = 60 * 1000;

/** Locked: the timer half of the trigger. */
export const SHOW_AFTER_MS = 10_000;

/** Locked: the scroll half of the trigger. */
export const SHOW_AFTER_SCROLL_RATIO = 0.4;

// -----------------------------------------------------------------------------
// TYPES
// -----------------------------------------------------------------------------

export type AppBannerOs = 'android' | 'ios';
export type AppBannerPageType = 'property' | 'syndicated' | 'guide';
export type AppBannerAnswerReason = 'dismiss' | 'click';

/**
 * Which of the two banners the visitor sees.
 *
 *   'get'  — "Get the app": the phone does not have it, or we cannot tell and
 *            have no reason to think it does.
 *   'open' — "Open in app": the phone has it, or has most likely got it.
 */
export type AppBannerVariant = 'get' | 'open';

export interface AppBannerAnswer {
  /** Epoch milliseconds at which the visitor answered. */
  at: number;
  reason: AppBannerAnswerReason;
}

export interface AppBannerSession {
  /**
   * Epoch milliseconds the visitor was last known to be on the site. The idle
   * clock runs from here; the heartbeat in src/lib/appBannerSession.ts keeps
   * it fresh while a page is open and visible.
   */
  lastSeenAt: number;
  /** How the banner was answered in this session, or null while it has not been. */
  answer: AppBannerAnswer | null;
}

/** The three GA4 events, with the parameter set the ticket locked plus the variant. */
export interface AppBannerEventParams {
  page_type: AppBannerPageType;
  property_id: string;
  os: AppBannerOs;
  variant: AppBannerVariant;
}

export interface ScrollMetrics {
  scrollY: number;
  viewportHeight: number;
  documentHeight: number;
}

// -----------------------------------------------------------------------------
// AUDIENCE
// -----------------------------------------------------------------------------

/**
 * True when the user agent belongs to the Afriqahome native shell.
 *
 * WHY THIS EXISTS ALONGSIDE `isNative()`. src/lib/native.ts answers "is this
 * the Capacitor BUILD?" from `import.meta.env.VITE_BUILD_TARGET` — a fact about
 * the bundle, not about the webview running it. capacitor.config.ts lists
 * www.afriqahome.com in `allowNavigation`, so the app's webview can end up on
 * the real website, running the WEB bundle, where `isNative()` is correctly
 * false. That is the one place a "download our app" bar could appear inside the
 * app itself. The native shell appends `Afriqahome/iOS` / `Afriqahome/Android`
 * to its user agent (the same suffix src/lib/native.ts's `isAppReferrer` reads
 * server-side), so this catches it.
 */
export function isAfriqahomeAppUserAgent(userAgent: string): boolean {
  return /Afriqahome\/(iOS|Android)/.test(userAgent);
}

/**
 * The mobile OS this banner is allowed to speak to, or null for everyone else
 * (desktop, tablets we cannot place, bots, and the native shell above).
 *
 * `platform` and `maxTouchPoints` are optional and only decide the iPad case:
 * iPadOS 13+ ships a desktop Safari user agent — "Macintosh; Intel Mac OS X" —
 * and is distinguishable from a real Mac only by the touch points. An iPad that
 * can run the iOS app should be offered it, so it counts as 'ios'.
 */
export function detectMobileOs(
  userAgent: string,
  hints: { platform?: string; maxTouchPoints?: number } = {},
): AppBannerOs | null {
  if (!userAgent) return null;
  if (isAfriqahomeAppUserAgent(userAgent)) return null;

  // Android first: an Android UA never contains iPhone/iPad, but some Android
  // WebViews do carry "Safari" and "Mobile", so the reverse order would be
  // ambiguous for nobody — this order is simply the one that stays readable.
  if (/Android/i.test(userAgent)) return 'android';

  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'ios';

  // iPadOS 13+ in its default "desktop" mode.
  if (hints.platform === 'MacIntel' && (hints.maxTouchPoints ?? 0) > 1) return 'ios';

  return null;
}

// -----------------------------------------------------------------------------
// THE SESSION
// -----------------------------------------------------------------------------

export function serializeSession(session: AppBannerSession): string {
  return JSON.stringify({
    lastSeenAt: session.lastSeenAt,
    answer: session.answer ? { at: session.answer.at, reason: session.answer.reason } : null,
  });
}

/**
 * Reads whatever is under the storage key. Returns null for anything it cannot
 * make sense of, which means "no session on record" — the recoverable error.
 * Unparseable state must not silence the banner.
 *
 * The old cooldown format (a bare timestamp, or `{"at":…,"reason":…}`) has no
 * `lastSeenAt` and so parses as nothing. That is deliberate: a "no" given
 * under the 14-day rule is not a "no" in this session.
 */
export function parseSession(raw: string | null | undefined): AppBannerSession | null {
  if (raw == null) return null;

  const trimmed = raw.trim();
  if (trimmed === '') return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }

  if (typeof parsed !== 'object' || parsed === null) return null;

  const lastSeenAt = (parsed as { lastSeenAt?: unknown }).lastSeenAt;
  if (typeof lastSeenAt !== 'number' || !Number.isFinite(lastSeenAt)) return null;

  let answer: AppBannerAnswer | null = null;
  const rawAnswer = (parsed as { answer?: unknown }).answer;
  if (typeof rawAnswer === 'object' && rawAnswer !== null) {
    const at = (rawAnswer as { at?: unknown }).at;
    if (typeof at === 'number' && Number.isFinite(at)) {
      const reason = (rawAnswer as { reason?: unknown }).reason;
      answer = { at, reason: reason === 'click' ? 'click' : 'dismiss' };
    }
  }

  return { lastSeenAt, answer };
}

/**
 * Has this session ended by `now`?
 *
 * Only idleness ends a session — the age of the answer never does, so a
 * visitor who says no and keeps browsing is left alone for as long as they
 * browse (the heartbeat keeps `lastSeenAt` current).
 *
 * A `lastSeenAt` slightly in the future — a clock corrected backwards since the
 * last heartbeat — is still the same session: the conservative reading, since
 * the failure this guards is re-asking somebody who just said no. More than a
 * whole timeout into the future is not a skew, it is a record written under a
 * clock that was wrong, and honouring it could silence the banner for months.
 */
export function isSessionExpired(session: AppBannerSession, now: number): boolean {
  const idle = now - session.lastSeenAt;
  if (idle > SESSION_IDLE_TIMEOUT_MS) return true;
  if (idle < -SESSION_IDLE_TIMEOUT_MS) return true;
  return false;
}

/**
 * The session the stored value describes, or null when there is none: nothing
 * stored, nothing parseable, or a session that has since ended.
 */
export function readSession(raw: string | null | undefined, now: number): AppBannerSession | null {
  const session = parseSession(raw);
  if (!session) return null;
  return isSessionExpired(session, now) ? null : session;
}

/**
 * The session as it should be written after a sign of life at `now`. Passing
 * null — no current session — begins a new one, unanswered.
 */
export function touchSession(session: AppBannerSession | null, now: number): AppBannerSession {
  return { lastSeenAt: now, answer: session?.answer ?? null };
}

/**
 * The session as it should be written once the visitor has answered at `now`.
 * Nothing from the previous record survives an answer — the answer IS the
 * record — so this takes no session in, and it does not matter whether the one
 * on record had quietly expired under the visitor's thumb.
 */
export function answerSession(now: number, reason: AppBannerAnswerReason): AppBannerSession {
  return { lastSeenAt: now, answer: { at: now, reason } };
}

/**
 * The question the gate asks: has the visitor already answered the banner in
 * the session that is running at `now`? An ended session answers no.
 */
export function isAnsweredInSession(raw: string | null | undefined, now: number): boolean {
  return readSession(raw, now)?.answer != null;
}

// -----------------------------------------------------------------------------
// TRIGGER
// -----------------------------------------------------------------------------

/**
 * How far the visitor has travelled through the page: 0 at the top, 1 at the
 * bottom, whatever the page's length.
 *
 * NOT GA4's reporting definition — (scrollY + viewportHeight) / documentHeight,
 * "how far the bottom edge of the viewport has reached". That one is right for
 * milestone reporting and wrong for a trigger, because it is already above 0.4
 * BEFORE A SINGLE PIXEL OF SCROLLING on any page shorter than 2.5 viewports.
 * The /syndicated/:id layout is comfortably inside that on a phone, so the
 * scroll branch would have fired on the first scroll event the page ever
 * received — which is "on initial paint" wearing a different hat, and locked
 * decision 3 forbids exactly that.
 *
 * This definition is 0 at the top of every page, by construction.
 */
export function scrollDepthRatio(metrics: ScrollMetrics): number {
  const scrollable = metrics.documentHeight - metrics.viewportHeight;
  if (scrollable <= 0) return 0;
  const ratio = metrics.scrollY / scrollable;
  if (!Number.isFinite(ratio)) return 0;
  return Math.min(Math.max(ratio, 0), 1);
}

/**
 * The scroll half of the trigger.
 *
 * TWO GUARDS, BOTH THERE TO HONOUR "never on initial paint":
 *
 *   `scrollY <= 0`      — an unscrolled page never triggers, whatever the
 *                         arithmetic says, including a rubber-band overscroll
 *                         upwards on iOS.
 *   not scrollable      — a document no taller than the viewport has no depth
 *                         to measure. It gets the 10s timer only.
 */
export function isScrollTriggered(metrics: ScrollMetrics): boolean {
  if (metrics.scrollY <= 0) return false;
  if (metrics.documentHeight - metrics.viewportHeight <= 0) return false;
  return scrollDepthRatio(metrics) >= SHOW_AFTER_SCROLL_RATIO;
}

// -----------------------------------------------------------------------------
// THE GATE
// -----------------------------------------------------------------------------

export interface RenderDecisionInput {
  /** src/lib/native.ts `isNative()` — the Capacitor build flag. */
  isNativeApp: boolean;
  /** `detectMobileOs()` over the live user agent. */
  os: AppBannerOs | null;
  /** `isAnsweredInSession()` over the live storage value. */
  answeredThisSession: boolean;
  /** `hasIosAppStoreId()`. Only consulted when `os` is 'ios'. */
  iosStoreIdResolved: boolean;
}

/**
 * Every reason the banner may not exist, in one function so the component has
 * exactly one thing to ask and the test has exactly one thing to pin.
 */
export function shouldRenderBanner(input: RenderDecisionInput): boolean {
  if (input.isNativeApp) return false;
  if (input.os === null) return false;
  if (input.answeredThisSession) return false;
  if (input.os === 'ios' && !input.iosStoreIdResolved) return false;
  return true;
}

// -----------------------------------------------------------------------------
// THE VARIANT — "get the app" or "open in the app"
// -----------------------------------------------------------------------------

export interface VariantDecisionInput {
  /**
   * What the browser could establish. Chrome on Android answers
   * `navigator.getInstalledRelatedApps()` with a real true/false once the
   * Android app declares this site (android/app/src/main/res/values/strings.xml,
   * `asset_statements`) and public/manifest.webmanifest names the app. Every
   * other browser — all of iOS included, where no web API can see installed
   * apps — is null: unknown.
   */
  installed: boolean | null;
  /** The install hint: this browser has tapped the banner's CTA before. */
  tappedBefore: boolean;
}

/**
 * The browser's word is final where it has one. Where it has none, a phone
 * that has tapped "Get the app" before is the best evidence there is that the
 * app is now on it — the store, not us, saw whether the install happened. The
 * "open" copy is written so that it is not wrong if that guess is: on Android
 * the intent URL still falls through to the Play Store, and on iOS the CTA is
 * the App Store product page either way, which shows Open to a phone that has
 * the app and Get to one that does not.
 */
export function bannerVariant(input: VariantDecisionInput): AppBannerVariant {
  if (input.installed !== null) return input.installed ? 'open' : 'get';
  return input.tappedBefore ? 'open' : 'get';
}

/**
 * Chrome's answer to `getInstalledRelatedApps()`, reduced to "is OUR Android
 * app among them". The manifest may one day list more than one entry.
 */
export function includesAfriqahomeApp(apps: ReadonlyArray<{ platform?: string; id?: string }>): boolean {
  return apps.some((app) => app.platform === 'play' && app.id === ANDROID_PACKAGE_NAME);
}

// -----------------------------------------------------------------------------
// THE DESTINATION
// -----------------------------------------------------------------------------

/** True once IOS_APP_STORE_ID has been replaced by real digits. */
export function hasIosAppStoreId(id: string = IOS_APP_STORE_ID): boolean {
  return /^\d+$/.test(id);
}

export function iosAppStoreUrl(id: string = IOS_APP_STORE_ID): string {
  return `https://apps.apple.com/app/id${id}`;
}

/**
 * The Play Store fallback, carrying the Install Referrer the app will consume
 * in a later ticket.
 *
 * `referrer` is one query string nested inside another, so its `=` and `&` are
 * percent-encoded exactly once by the `encodeURIComponent` below — which is
 * also what encodes the property path's slashes. `path` arrives already
 * percent-encoded (it comes from `window.location.pathname`, which the browser
 * normalises), so a slug with non-ASCII characters is encoded twice and must be
 * decoded twice on the app side. That is the price of nesting and it is
 * lossless in both directions.
 */
export function playStoreUrl(path: string): string {
  const referrer = `utm_source=web_banner&utm_medium=smart_banner&property_path=${path}`;
  return (
    `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE_NAME}` +
    `&referrer=${encodeURIComponent(referrer)}`
  );
}

/**
 * The Android intent URL.
 *
 * `path` is NOT encoded into the `intent://` host+path — it is a real URL path
 * there, and encoding its slashes would produce a target the App Link
 * association cannot match. It IS encoded inside `S.browser_fallback_url`,
 * where the whole Play Store URL is one parameter value; leaving its `&` raw
 * would terminate the intent's `S.` parameter at the first ampersand and drop
 * the referrer.
 */
export function androidIntentUrl(path: string): string {
  const fallback = encodeURIComponent(playStoreUrl(path));
  return (
    `intent://${APP_LINK_HOST}${path}#Intent` +
    `;scheme=https` +
    `;package=${ANDROID_PACKAGE_NAME}` +
    `;S.browser_fallback_url=${fallback}` +
    `;end`
  );
}

/**
 * Does the native app claim this path?
 *
 * Read straight off REL-06's allowlist rather than re-listed here, because that
 * list is already a checked mirror of the two OS artifacts —
 * android/app/src/main/AndroidManifest.xml and
 * public/.well-known/apple-app-site-association — and
 * nativeDeepLinkRouting.test.ts fails if the three ever diverge. A second copy
 * in this file would be a fourth thing to keep in step, and the first to rot.
 *
 * The consequence is that the day someone claims `/guides/` in the app, this
 * banner starts emitting intent URLs for guides with no change here.
 */
export function isDeepLinkablePath(path: string): boolean {
  const pathname = path.split(/[?#]/)[0];
  return (
    DEEP_LINK_ALLOWLIST.exact.includes(pathname) ||
    DEEP_LINK_ALLOWLIST.prefix.some((prefix) => pathname.startsWith(prefix))
  );
}

/**
 * Where the CTA goes, for the OS we detected. The same destination for both
 * variants: "open" and "get" differ in what they say, not in where they send.
 *
 * ANDROID, AND WHY IT IS NOT ALWAYS AN INTENT URL. The intent URL's whole job
 * is "open the app on THIS page, or fall back to the store". It can only do the
 * first half on a path the app claims. `/guides/*` is not claimed — not in
 * AndroidManifest.xml, not in the AASA, not in lib/deepLinkRoutes.ts — so an
 * intent naming `com.afriqahome.app` for a guide URL resolves to no activity
 * even on a phone that HAS the app, and Chrome drops to
 * `S.browser_fallback_url`. Same destination, one failed activity launch and a
 * scheme some Android browsers handle poorly, for nothing. So an unclaimed path
 * links straight to the Play Store — carrying the same install referrer, so the
 * deferred-deep-link ticket can still send that reader to the guide they were
 * on once the app consumes it.
 *
 * iOS gets a plain App Store product-page link either way — deliberately not a
 * Universal Link back into the site (Safari does not open the app for a link
 * to the domain it is already on), and deliberately not an `apple-itunes-app`
 * meta tag (locked decision 6: custom banner only).
 */
export function appCtaHref(os: AppBannerOs, path: string, iosId: string = IOS_APP_STORE_ID): string {
  if (os === 'ios') return iosAppStoreUrl(iosId);
  return isDeepLinkablePath(path) ? androidIntentUrl(path) : playStoreUrl(path);
}

/**
 * `window.location.pathname` is the source of truth, but it is worth being
 * defensive about the two shapes that would produce a broken deep link: an
 * empty value, and one missing its leading slash.
 */
export function normalizeBannerPath(pathname: string | null | undefined): string {
  if (!pathname) return '/';
  return pathname.startsWith('/') ? pathname : `/${pathname}`;
}
